import { Inject, Injectable } from '@angular/core';
import { Theme } from '@hs/ui-core-presentation';
import * as htmlToImage from 'html-to-image';
import { jsPDF } from 'jspdf';

import { JSPDF_TOKEN } from '../models/jspdf-token';
import { PdfExportConfig } from '../models/pdf-export-config';

@Injectable()
export class PDFExportService {
    private readonly config: PdfExportConfig = {
        mode: 'l',
        unit: 'pt',
        format: 'a4',
        pdfImageFormat: 'jpeg',
    };

    constructor(@Inject(JSPDF_TOKEN) private readonly jsPdfToken: typeof jsPDF) {}

    async generatePDF(
        element: HTMLElement,
        fileName: string,
        width: number,
        height: number,
        theme: Theme = Theme.light,
    ): Promise<void> {
        try {
            const canvas = await htmlToImage.toCanvas(element, {
                width,
                height,
                cacheBust: true,
                backgroundColor: theme === Theme.light ? '#fff' : '#1f2122',
            });

            this.saveImageToPDF(canvas, fileName, width, height, theme);

            const clonedDashboard = document.querySelector('#cloned-dashboard');
            if (clonedDashboard) {
                document.body.removeChild(clonedDashboard);
            }
        } catch (e) {
            throw new Error(`PDF export could not be completed. Please retry or contact support if the issue persists: ${(e as Error).message}`);
        }
    }

    private saveImageToPDF(data: HTMLCanvasElement, fileName: string, width: number, height: number, theme: Theme = Theme.light): void {
        const doc = new this.jsPdfToken(this.config.mode, this.config.unit, this.config.format);
        const pdfWidth = doc.internal.pageSize.width;
        const pdfHeight = doc.internal.pageSize.height;
        const imageSizeForPDF = this.getImageSizeToFitInPage(pdfWidth, pdfHeight, width, height);

        doc.setFillColor(theme === Theme.light ? '#fff' : '#1f2122');
        doc.rect(0, 0, pdfWidth, pdfHeight, 'f');
        doc.addImage(data, this.config.pdfImageFormat, 0, 0, imageSizeForPDF.width, imageSizeForPDF.height);
        doc.save(`${fileName}.pdf`);
    }

    private getImageSizeToFitInPage(
        availableWidth: number,
        availableHeight: number,
        imageWidth: number,
        imageHeight: number,
    ): { height: number, width: number } {
        const aspectRatio = imageWidth / imageHeight;
        let height = imageHeight;
        let width = imageWidth;
        if (imageHeight > availableHeight && (imageWidth <= availableWidth)) {
            height = availableHeight;
            width = height * aspectRatio;
        } else if (width > availableWidth && (height <= availableHeight)) {
            width = availableWidth;
            height = width / aspectRatio;
        } else if (height > availableHeight && width > availableWidth) {
            const percentageDecreaseInHeight = (height - availableHeight) / height;
            const percentageDecreaseInWidth = (width - availableWidth) / width;

            if (percentageDecreaseInHeight > percentageDecreaseInWidth) {
                height = availableHeight;
                width = height * aspectRatio;
            } else {
                width = availableWidth;
                height = width / aspectRatio;
            }
        } else if (height < availableHeight && width < availableWidth) {
            const percentageIncreaseInHeight = (availableHeight - height) / height;
            const percentageIncreaseInWidth = (availableWidth - width) / width;

            if (percentageIncreaseInHeight < percentageIncreaseInWidth) {
                height = availableHeight;
                width = height * aspectRatio;
            } else {
                width = availableWidth;
                height = width / aspectRatio;
            }
        }
        return { height, width };
    }
}

